import React, { useEffect, useId, useState } from "react"
import { graphql } from "gatsby"
import * as styles from "./luas-stop-times-module.module.scss"
import {
  formatDueMins,
  lineSlug,
  loadForecast,
  loadStops,
  LuasForecast,
  LuasLine,
  readStopQuery,
  resolveSelection,
  writeStopQuery,
} from "./luas-api"
import LuasStopSelector from "./luas-stop-selector"
import { getLuasUiLabels, LuasUiLabelsRecord } from "../luas/luas-ui-labels"

type RenderProps = {
  label?: string | null
  locale?: string
  luasUiLabels?: LuasUiLabelsRecord | null
}

const LuasStopTimesModule: React.FC<RenderProps> = ({ label, luasUiLabels }) => {
  const uiLabels = getLuasUiLabels(luasUiLabels)
  const directionHeadingId = useId()
  const [lines, setLines] = useState<LuasLine[]>([])
  const [selectedLine, setSelectedLine] = useState("")
  const [selectedStop, setSelectedStop] = useState("")
  const [forecast, setForecast] = useState<LuasForecast | null>(null)
  const [loading, setLoading] = useState(false)
  const [stopsError, setStopsError] = useState<string | null>(null)
  const [forecastError, setForecastError] = useState<string | null>(null)

  const fetchForecast = async (stopAbrev: string) => {
    setLoading(true)
    setForecastError(null)
    setForecast(null)
    try {
      setForecast(await loadForecast(stopAbrev))
    } catch {
      setForecastError(uiLabels.forecastUnavailable)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false

    loadStops()
      .then(loadedLines => {
        if (cancelled) return
        setLines(loadedLines)

        const query = readStopQuery()
        const selection = resolveSelection(loadedLines, query.line, query.stop)
        setSelectedLine(selection.lineName)
        setSelectedStop(selection.stopAbrev)
        if (selection.stopAbrev) fetchForecast(selection.stopAbrev)
      })
      .catch(() => {
        if (!cancelled) setStopsError(uiLabels.stopsUnavailable)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const handleStopChange = (stopAbrev: string) => {
    setSelectedStop(stopAbrev)
  }

  const handleLineChange = (lineName: string) => {
    setSelectedLine(lineName)
    setSelectedStop("")
    setForecast(null)
    setForecastError(null)
    writeStopQuery(lineName ? lineSlug(lineName) : "", "")
  }

  const handleFind = () => {
    if (!selectedStop) return
    writeStopQuery(lineSlug(selectedLine), selectedStop)
    fetchForecast(selectedStop)
  }

  return (
    <section className={styles.page} aria-label={label || uiLabels.stopInformation}>
      <aside className={styles.sidebar}>
        <h2 className={styles.sidebarTitle}>{label || uiLabels.stopInformation}</h2>

        <LuasStopSelector
          lines={lines}
          lineName={selectedLine}
          stopAbrev={selectedStop}
          onLineChange={handleLineChange}
          onStopChange={handleStopChange}
          error={stopsError}
          labels={uiLabels}
          legend={label || uiLabels.stopInformation}
          submitLabel={uiLabels.find}
          submitDisabled={loading}
          onSubmitClick={handleFind}
        />
      </aside>

      <div className={styles.main}>
        <p className={loading ? styles.loading : "sr-only"} role="status" aria-live="polite" aria-atomic="true">
          {loading
            ? uiLabels.loadingForecasts
            : forecast
              ? uiLabels.forecastLoadedAnnouncement
                  .replace("{stop}", forecast.stop)
                  .replace("{directions}", String(forecast.directions.length))
              : ""}
        </p>
        {forecastError && (
          <p className={styles.error} role="alert" aria-atomic="true">
            {forecastError}
          </p>
        )}

        {forecast && (
          <>
            <h2 className={styles.stopName}>{forecast.stop}</h2>
            {forecast.message && <p className={styles.message}>{forecast.message}</p>}

            <div className={styles.directions}>
              {forecast.directions.map((direction, index) => (
                <div key={direction.name} className={styles.directionBlock}>
                  <h3 id={`${directionHeadingId}-${index}`} className={styles.directionName}>
                    {direction.name}
                  </h3>
                  <div className={styles.tableWrapper}>
                    <table className={styles.table} aria-labelledby={`${directionHeadingId}-${index}`}>
                      <thead>
                        <tr>
                          <th scope="col">{uiLabels.destination}</th>
                          <th scope="col">{uiLabels.minutes}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {direction.trams.length > 0 ? (
                          direction.trams.map((tram, index) => (
                            <tr key={`${tram.destination}-${tram.dueMins}-${index}`}>
                              <td>{tram.destination}</td>
                              <td>{formatDueMins(tram.dueMins)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={2}>{uiLabels.noTramsScheduled}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export const fragments = graphql`
  fragment LuasStopTimesModuleFields on DatoCmsLuasStopTimesModule {
    __typename
    id
    label
  }
`

export default LuasStopTimesModule
